<template>
  <div v-if="isLoading" class="h-8 flex justify-center items-center">
    <lottie :options="loadingAnimationOptions" />
  </div>
  <div v-else-if="connection">
    <div class="flex items-center border-b border-truegray-300 mb-12 pb-8">
      <div class="w-2/5">
        <h6 class="font-nunito text-cw-300 mb-3">Connection</h6>
        <h3 class="font-montserrat pr-16 mb-0">
          {{ connection.name }}
        </h3>
      </div>
      <div class="w-3/5 flex gap-20">
        <div class="flex flex-col justify-center pl-1">
          <h6 class="text-truegray-300">Job title</h6>
          <h6 class="text-sm text-truegray-500 truncate overflow-ellipsis">
            {{ connection.job_title }}
          </h6>
        </div>
        <div>
          <AuthorBadge
            :entity="connection.organisation"
            :context="'connections'"
          />
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 mb-16">
      <div class="col-span-1 flex flex-col items-center justify-center">
        <h5 class="text-cw-900 font-nunito pb-2 mb-4">Total Views</h5>
        <div class="flex space-x-4 items-center">
          <img
            :style="{ maxWidth: '65px' }"
            src="/img/icons/contributor_dashboard/impressions-gradient.svg"
            alt="Views icon"
          />

          <h1 class="font-montserrat font-black cw-gradient-text m-0">
            {{ formatNumber(connection.statistics.totals.displayed) }}
          </h1>
        </div>
      </div>

      <div class="col-span-1 flex flex-col items-center justify-center">
        <h5 class="text-cw-900 font-nunito pb-2 mb-4">Total Leads</h5>
        <div class="flex items-center space-x-4">
          <img
            :style="{ maxWidth: '125px' }"
            src="/img/icons/contributor_dashboard/views-gradient.svg"
            alt="Leads icon"
          />

          <h1 class="font-montserrat font-black cw-gradient-text m-0">
            {{ formatNumber(connection.statistics.totals.viewed) }}
          </h1>
        </div>
      </div>
    </div>

    <div
      class="grid grid-cols-3 gap-12"
      :class="{ 'border-b border-truegray-200 pb-6': hasSchoolsStats }"
    >
      <div class="col-span-1 flex flex-col items-center justify-center">
        <h5 class="text-cw-900 font-nunito pb-2 mb-4">Total Messaged</h5>
        <div class="flex space-x-4 items-center mb-6">
          <img
            :style="{ width: '65px' }"
            src="/img/icons/contributor_dashboard/mail-gradient.svg"
            alt="Mail icon"
          />

          <h1 class="font-montserrat font-black cw-gradient-text m-0">
            {{ formatNumber(connection.statistics.totals.messaged) }}
          </h1>
        </div>
      </div>

      <div class="col-span-1 flex flex-col items-center justify-center">
        <h5 class="text-cw-900 font-nunito pb-2 mb-4">Total Phoned</h5>
        <div class="flex space-x-4 items-center mb-6">
          <img
            :style="{ width: '65px' }"
            src="/img/icons/contributor_dashboard/phone-gradient.svg"
            alt="Phone icon"
          />

          <h1 class="font-montserrat font-black cw-gradient-text m-0">
            {{ formatNumber(connection.statistics.totals.phoned) }}
          </h1>
        </div>
      </div>

      <div class="col-span-1 flex flex-col items-center justify-center">
        <h5 class="text-cw-900 font-nunito pb-2 mb-4">Total LinkedIn Views</h5>
        <div class="flex space-x-4 items-center mb-6">
          <img
            :style="{ width: '65px' }"
            src="/img/icons/contributor_dashboard/linkedin-gradient.svg"
            alt="LinkedIn icon"
          />

          <h1 class="font-montserrat font-black cw-gradient-text m-0">
            {{ formatNumber(connection.statistics.totals.linkedIn) }}
          </h1>
        </div>
      </div>
    </div>

    <div
      v-if="hasPostStatistics"
      :class="postsStatsOpen ? 'pt-3 pb-4' : 'py-2'"
      class="bg-cw-50 rounded mt-7 px-4"
    >
      <div
        :class="isLoading ? 'cursor-not-allowed pointer-events-none' : ''"
        class="flex items-center justify-between transition-all rounded cursor-pointer group"
        @click="postsStatsOpen = !postsStatsOpen"
      >
        <div class="flex gap-3">
          <img class="w-4 h-4" src="/img/logo-cw.png" alt="CareerWise logo" />
          <p class="text-xs">
            Cloud posts this connection has been viewed from:
          </p>
        </div>

        <div
          :class="postsStatsOpen ? 'rotate-90 text-cw-alt' : ''"
          class="group-hover:text-cw-alt transition-all"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            :class="postsStatsOpen ? 'w-4 h-4' : 'w-5 h-5'"
            class="transition-all"
          >
            <path
              fill-rule="evenodd"
              d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clip-rule="evenodd"
            />
            <path
              fill-rule="evenodd"
              d="M4.21 14.77a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>

      <div
        :class="
          postsStatsOpen ? 'h-auto pt-6 pb-4 px-4 mt-2' : 'h-0 overflow-hidden'
        "
        class="w-full bg-white rounded"
      >
        <div class="grid grid-cols-8 items-center">
          <div class="col-span-4 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-4">Post</h6>
          </div>

          <div class="col-span-3 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-6">School</h6>
          </div>

          <div class="col-span-1 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-3">Viewed by</h6>
          </div>
        </div>

        <div class="max-h-56 overflow-y-scroll">
          <div
            v-for="(postStats, i) in postsStats"
            :key="i"
            class="grid grid-cols-8 gap-2 items-center my-2"
          >
            <div
              class="col-span-4 flex space-x-4 items-center"
              :title="postStats.post.title"
            >
              <p
                v-if="postStats.post.title"
                class="text-truegray-500 text-xs truncate overflow-ellipsis pl-2"
              >
                {{ postStats.post.title }}
              </p>
            </div>

            <div
              v-if="postStats.school"
              class="col-span-3 flex items-center space-x-4"
              :title="postStats.school.profile_title"
            >
              <img
                v-if="postStats.school.logo"
                :src="postStats.school.logo"
                class="w-6 h-6 text-xs"
                alt="School logo"
              />
              <span
                class="text-truegray-500 text-xs truncate overflow-ellipsis"
              >
                {{ getProfileTitle(postStats.school) }}
              </span>
            </div>
            <div
              v-else
              class="col-span-3 flex items-center space-x-4"
              title="Viewed on the Cloud"
            >
              <img
                src="/img/fav_icon/cw/favicon-32x32.png"
                class="w-6 h-6"
                alt="CareerWise logo"
              />
              <span
                class="text-truegray-500 text-xs truncate overflow-ellipsis"
              >
                Cloud post
              </span>
            </div>

            <div class="col-span-1 flex items-center space-x-4">
              <img
                src="/img/icons/contributor_dashboard/member-gradient.svg"
                alt="Count icon"
              />
              <span class="text-truegray-500 text-xs">
                {{ formatNumber(postStats.count) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Views by school -->
    <div
      v-if="hasImpressionsStatistics"
      :class="impressionsStatsOpen ? 'pt-3 pb-4' : 'py-2'"
      class="bg-cw-50 rounded mt-7 px-4"
    >
      <div
        :class="isLoading ? 'cursor-not-allowed pointer-events-none' : ''"
        class="flex items-center justify-between transition-all rounded cursor-pointer group"
        @click="impressionsStatsOpen = !impressionsStatsOpen"
      >
        <div class="flex gap-3">
          <img
            src="/img/icons/contributor_dashboard/impressions-gradient.svg"
            alt="Impressions icon"
            class="w-4 h-4"
          />
          <p class="text-xs">Views by school:</p>
        </div>

        <div
          :class="impressionsStatsOpen ? 'rotate-90 text-cw-alt' : ''"
          class="group-hover:text-cw-alt transition-all"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            :class="impressionsStatsOpen ? 'w-4 h-4' : 'w-5 h-5'"
            class="transition-all"
          >
            <path
              fill-rule="evenodd"
              d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clip-rule="evenodd"
            />
            <path
              fill-rule="evenodd"
              d="M4.21 14.77a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>

      <div
        :class="
          impressionsStatsOpen
            ? 'h-auto pt-6 pb-4 px-4 mt-2'
            : 'h-0 overflow-hidden'
        "
        class="w-full bg-white rounded"
      >
        <div class="grid grid-cols-3 items-center">
          <div class="col-span-2 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-6">School</h6>
          </div>

          <div class="col-span-1 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-3">Total views</h6>
          </div>
        </div>

        <div class="max-h-56 overflow-y-scroll">
          <div
            v-for="(impressionsStat, i) in impressionsStats"
            :key="i"
            class="grid grid-cols-3 gap-2 items-center my-2"
          >
            <div
              v-if="impressionsStat.school"
              class="col-span-2 flex items-center space-x-4"
              :title="impressionsStat.school.profile_title"
            >
              <img
                :src="impressionsStat.school.logo"
                class="w-6 h-6 text-xs"
                alt="School logo"
              />
              <span
                class="text-truegray-500 text-xs truncate overflow-ellipsis"
              >
                {{ getProfileTitle(impressionsStat.school) }}
              </span>
            </div>

            <div class="col-span-1 flex items-center space-x-4">
              <img
                src="/img/icons/contributor_dashboard/member-gradient.svg"
                alt="Count icon"
              />
              <span class="text-truegray-500 text-xs">
                {{ formatNumber(impressionsStat.count) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- /Views by school -->

    <!-- Leads by school -->
    <div
      v-if="hasViewsStatistics"
      :class="viewsStatsOpen ? 'pt-3 pb-4' : 'py-2'"
      class="bg-cw-50 rounded mt-7 px-4"
    >
      <div
        :class="isLoading ? 'cursor-not-allowed pointer-events-none' : ''"
        class="flex items-center justify-between transition-all rounded cursor-pointer group"
        @click="viewsStatsOpen = !viewsStatsOpen"
      >
        <div class="flex gap-3">
          <img
            src="/img/icons/contributor_dashboard/views-gradient.svg"
            alt="Views icon"
            class="w-4 h-4"
          />
          <p class="text-xs">Leads by school:</p>
        </div>

        <div
          :class="viewsStatsOpen ? 'rotate-90 text-cw-alt' : ''"
          class="group-hover:text-cw-alt transition-all"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            :class="viewsStatsOpen ? 'w-4 h-4' : 'w-5 h-5'"
            class="transition-all"
          >
            <path
              fill-rule="evenodd"
              d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clip-rule="evenodd"
            />
            <path
              fill-rule="evenodd"
              d="M4.21 14.77a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
      </div>

      <div
        :class="
          viewsStatsOpen ? 'h-auto pt-6 pb-4 px-4 mt-2' : 'h-0 overflow-hidden'
        "
        class="w-full bg-white rounded"
      >
        <div class="grid grid-cols-3 items-center">
          <div class="col-span-2 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-6">School</h6>
          </div>

          <div class="col-span-1 pb-1 mb-4 border-b border-truegray-700">
            <h6 class="font-nunito pl-3">Total leads</h6>
          </div>
        </div>

        <div class="max-h-56 overflow-y-scroll">
          <div
            v-for="(viewsStat, i) in viewsStats"
            :key="i"
            class="grid grid-cols-3 gap-2 items-center my-2"
          >
            <div
              class="col-span-2 flex items-center space-x-4"
              :title="viewsStat.school.profile_title"
            >
              <img
                v-if="viewsStat.school.logo"
                :src="viewsStat.school.logo"
                class="w-6 h-6 text-xs"
                alt="School logo"
              />
              <span
                class="text-truegray-500 text-xs truncate overflow-ellipsis"
              >
                {{ getProfileTitle(viewsStat.school) }}
              </span>
            </div>

            <div class="col-span-1 flex items-center space-x-4">
              <img
                src="/img/icons/contributor_dashboard/member-gradient.svg"
                alt="Count icon"
              />
              <span class="text-truegray-500 text-xs">
                {{ formatNumber(viewsStat.count) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- /Leads by school -->
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useStore } from '../../../../js/store/index.js'
import AuthorBadge from '../../../tenant/manager/pages/cloud/AuthorBadge.vue'
import { formatNumber } from '../../../../js/util/helper.js'
import * as loadingAnimation from '../../../../shared/components/loading-dark.json'

export default defineComponent({
  name: 'ModalConnectionStats',

  components: {
    AuthorBadge,
  },

  props: {
    config: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    const store = useStore()

    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isLoading = ref(false)

    const connection = ref(null)
    const postsStats = computed(() => {
      return Object.values(connection.value?.statistics?.posts).filter(
        (stats) => stats.post,
      )
    })
    const impressionsStats = computed(() => {
      return Object.values(connection.value?.statistics?.impressions)
    })
    const viewsStats = computed(() => {
      return Object.values(connection.value?.statistics?.views)
    })

    const hasPostStatistics = computed(() => postsStats.value?.length)
    const hasImpressionsStatistics = computed(
      () => impressionsStats.value?.length,
    )
    const hasViewsStatistics = computed(() => viewsStats.value?.length)
    const postsStatsOpen = ref(false)
    const impressionsStatsOpen = ref(false)
    const viewsStatsOpen = ref(false)

    const hasSchoolsStats = computed(
      () =>
        hasPostStatistics.value ||
        hasImpressionsStatistics.value ||
        hasViewsStatistics.value,
    )

    const getProfileTitle = (school) => {
      const isTrashed = school.is_trashed
      return `${school.profile_title}${isTrashed ? ' (inactive school)' : ''}`
    }

    const init = async () => {
      isLoading.value = true
      const payload = props.config?.connection?.id
      connection.value = await store.dispatch(
        'contributors/dispatchGetStatsConnection',
        payload,
      )
      isLoading.value = false
    }

    onMounted(init)

    return {
      formatNumber,
      loadingAnimationOptions,
      isLoading,

      connection,
      postsStats,
      impressionsStats,
      viewsStats,
      hasPostStatistics,
      hasImpressionsStatistics,
      hasViewsStatistics,
      hasSchoolsStats,
      postsStatsOpen,
      impressionsStatsOpen,
      viewsStatsOpen,

      getProfileTitle,
      init,
    }
  },
})
</script>
